import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Save,
  X,
  Users,
  Briefcase,
  MapPin,
  DollarSign,
  FileText,
  Building2,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { isClientReadyForRecruitment } from '@core/clients/clientEngagementGate';
import { clientService } from '@core/supabase/services/clientService';
import { orgService } from '@core/supabase/services/orgService';
import { requisitionService } from '@core/supabase/services/requisitionService';
import type { Client, Requisition, RequisitionInsert } from '@core/types';
import { REQUISITION_PRIORITIES, type RequisitionPriority } from '@core/enums';
import { AssistantIntake } from '@modules/agent/components/AssistantIntake';
import { INTAKE_CONFIGS } from '@modules/agent/intake.config';

const REQ_INTAKE_CFG =
  INTAKE_CONFIGS.find((c) => c.route === '/requisitions/new') ?? null;

const BRAND = '#8B80F9';

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  low:    { label: 'Low',    color: '#6b7280', bg: '#6b728015', border: '#6b728040' },
  normal: { label: 'Normal', color: '#10b981', bg: '#10b98115', border: '#10b98140' },
  high:   { label: 'High',   color: '#f59e0b', bg: '#f59e0b15', border: '#f59e0b40' },
  urgent: { label: 'Urgent', color: '#ef4444', bg: '#ef444415', border: '#ef444440' },
};

const WIZARD_STEPS = [
  { n: 1, title: 'Client & Role',      icon: Building2,  subtitle: 'Who is hiring and what is the role?' },
  { n: 2, title: 'People & Location',  icon: Users,      subtitle: 'Hiring manager and work arrangement.' },
  { n: 3, title: 'Role Scope',         icon: Briefcase,  subtitle: 'Justification and required skills.' },
  { n: 4, title: 'Compensation Band',  icon: DollarSign, subtitle: 'Budget expectations — internal only.' },
  { n: 5, title: 'Review',             icon: FileText,   subtitle: 'Confirm details and finish intake.' },
] as const;

type WizardForm = {
  client_id: string;
  title: string;
  department: string;
  headcount: string;
  priority: RequisitionPriority;
  hiring_manager_name: string;
  hiring_manager_email: string;
  location: string;
  employment_type: string;
  target_start_date: string;
  /** Phase 8 · C-37 — required; defaults to today + org default_tat_days (30). */
  target_close_date: string;
  business_justification: string;
  must_have_skills: string[];
  nice_to_have_skills: string[];
  budget_currency: string;
  budget_min: string;
  budget_max: string;
  internal_notes: string;
};

function defaultTargetCloseDate(tatDays = 30): string {
  const d = new Date();
  d.setDate(d.getDate() + tatDays);
  return d.toISOString().slice(0, 10);
}

function emptyForm(): WizardForm {
  return {
    client_id: '', title: '', department: '', headcount: '1', priority: 'normal',
    hiring_manager_name: '', hiring_manager_email: '',
    location: '', employment_type: '', target_start_date: '',
    target_close_date: '',
    business_justification: '',
    must_have_skills: [], nice_to_have_skills: [],
    budget_currency: 'INR', budget_min: '', budget_max: '',
    internal_notes: '',
  };
}

function fromReq(r: Requisition): WizardForm {
  return {
    client_id: r.client_id,
    title: r.title ?? '',
    department: r.department ?? '',
    headcount: String(r.headcount ?? 1),
    priority: r.priority ?? 'normal',
    hiring_manager_name: r.hiring_manager_name ?? '',
    hiring_manager_email: r.hiring_manager_email ?? '',
    location: r.location ?? '',
    employment_type: r.employment_type ?? '',
    target_start_date: r.target_start_date?.slice(0, 10) ?? '',
    target_close_date: r.target_close_date?.slice(0, 10) ?? '',
    business_justification: r.business_justification ?? '',
    must_have_skills: [...(r.must_have_skills ?? [])],
    nice_to_have_skills: [...(r.nice_to_have_skills ?? [])],
    budget_currency: r.budget_currency ?? 'INR',
    budget_min: r.budget_min != null ? String(r.budget_min) : '',
    budget_max: r.budget_max != null ? String(r.budget_max) : '',
    internal_notes: r.internal_notes ?? '',
  };
}

function toBaseFields(f: WizardForm) {
  return {
    title: f.title.trim(),
    department: f.department.trim() || null,
    headcount: Math.max(1, parseInt(f.headcount, 10) || 1),
    priority: f.priority,
    status: 'draft' as const,
    hiring_manager_name: f.hiring_manager_name.trim() || null,
    hiring_manager_email: f.hiring_manager_email.trim() || null,
    budget_currency: f.budget_currency.trim() || 'INR',
    budget_min: f.budget_min ? parseInt(f.budget_min, 10) : null,
    budget_max: f.budget_max ? parseInt(f.budget_max, 10) : null,
    location: f.location.trim() || null,
    employment_type: f.employment_type.trim() || null,
    business_justification: f.business_justification.trim() || null,
    internal_notes: f.internal_notes.trim() || null,
    target_start_date: f.target_start_date || null,
    target_close_date: f.target_close_date || null,
    must_have_skills: f.must_have_skills,
    nice_to_have_skills: f.nice_to_have_skills,
  };
}

function formatBudget(min: string, max: string, currency: string): string {
  const fmt = (v: string) => {
    const n = parseInt(v, 10);
    if (isNaN(n)) return null;
    return n.toLocaleString('en-IN');
  };
  const lo = fmt(min);
  const hi = fmt(max);
  if (!lo && !hi) return '—';
  if (lo && hi) return `${currency} ${lo} – ${hi} per annum`;
  if (lo) return `${currency} ${lo}+ per annum`;
  return `up to ${currency} ${hi} per annum`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (s: string) => UUID_RE.test(s);

export type IntakeLocationState = { intakeSeed?: Requisition; from?: string };

/** Section card wrapper for form groups */
function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-[var(--vscode-border-primary)] overflow-hidden"
      style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
      <div className="px-4 py-2.5 border-b border-[var(--vscode-border-primary)]"
        style={{ backgroundColor: 'var(--vscode-bg-secondary)' }}>
        <p className="text-xs font-semibold tracking-wide vscode-text-secondary uppercase">{title}</p>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

/** Review row */
function ReviewRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-widest vscode-text-tertiary mb-0.5">{label}</dt>
      <dd className="text-sm vscode-text-primary font-medium">{value || '—'}</dd>
    </div>
  );
}

type IntakeWizardPageProps = {
  /** When provided the component acts as a true modal — Close calls this instead of navigating */
  onModalClose?: () => void;
  /** Override the req ID (used when opened as a modal without routing) */
  modalReqId?: string;
  /** Pre-loaded seed when opening as modal */
  modalSeed?: Requisition;
};

export function RequisitionIntakeWizardPage({ onModalClose, modalReqId, modalSeed }: IntakeWizardPageProps = {}) {
  const params = useParams<{ id: string }>();
  const routeDraftId = modalReqId ?? params.id;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const originRef = useRef<string>(
    (location.state as IntakeLocationState | null)?.from ?? '/requisitions',
  );

  const [clients, setClients] = useState<Client[]>([]);
  const [defaultTatDays, setDefaultTatDays] = useState(30);
  const [form, setForm] = useState<WizardForm>(emptyForm());
  const [step, setStep] = useState(1);
  const [draftId, setDraftId] = useState<string | null>(routeDraftId ?? null);
  const [loading, setLoading] = useState(!!routeDraftId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mustInput, setMustInput] = useState('');
  const [niceInput, setNiceInput] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);

  const isResumeRoute = Boolean(routeDraftId);

  useEffect(() => {
    if (!user?.orgId) return;
    clientService.list(user.orgId).then((r) => setClients(Array.isArray(r) ? r : (r as { data: Client[] }).data));
    void orgService.getDefaultTatDays(user.orgId).then(setDefaultTatDays);
  }, [user?.orgId]);

  // Auto-fill target_close_date with the org default when the user first reaches step 2.
  useEffect(() => {
    if (step === 2 && !form.target_close_date) {
      setForm((f) => ({ ...f, target_close_date: defaultTargetCloseDate(defaultTatDays) }));
    }
  }, [step, defaultTatDays]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!routeDraftId || !user?.orgId) { setLoading(false); return; }
    if (!isUuid(routeDraftId)) { if (!onModalClose) navigate('/requisitions/new', { replace: true }); return; }

    const seed = modalSeed ?? (location.state as IntakeLocationState | null)?.intakeSeed;
    if (seed && seed.id === routeDraftId && seed.status === 'draft') {
      setForm(fromReq(seed));
      setDraftId(seed.id);
      setStep(Math.min(5, Math.max(1, seed.intake_wizard_step ?? 1)));
      setLoading(false);
      setError('');
      void requisitionService.getById(routeDraftId).then((fresh) => {
        if (!fresh) return;
        if (fresh.status !== 'draft') { navigate(`/requisitions/${routeDraftId}`, { replace: true }); return; }
        setForm(fromReq(fresh));
        setDraftId(fresh.id);
        setStep(Math.min(5, Math.max(1, fresh.intake_wizard_step ?? 1)));
      });
      return;
    }

    let cancelled = false;
    setLoading(true);
    requisitionService.getById(routeDraftId).then((r) => {
      if (cancelled) return;
      if (!r) { setError('Could not load this requisition. Use Retry or go back to the list.'); setLoading(false); return; }
      if (r.status !== 'draft') { navigate(`/requisitions/${routeDraftId}`, { replace: true }); return; }
      setForm(fromReq(r));
      setDraftId(r.id);
      setStep(Math.min(5, Math.max(1, r.intake_wizard_step ?? 1)));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [routeDraftId, user?.orgId, navigate, location.pathname, location.key, loadAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (field: keyof WizardForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const selectedClient = useMemo(() => clients.find((c) => c.id === form.client_id), [clients, form.client_id]);

  const persist = useCallback(async (nextStep: number | null, opts?: { finish?: boolean }) => {
    if (!user?.orgId) return null;
    setSaving(true); setError('');
    try {
      const fields = toBaseFields(form);
      const stepVal = opts?.finish ? null : nextStep;
      if (draftId) {
        const updated = await requisitionService.update(draftId, {
          ...fields, client_id: form.client_id, intake_wizard_step: stepVal,
        } as Partial<Requisition>);
        return updated;
      }
      const insert: RequisitionInsert = {
        org_id: user.orgId, client_id: form.client_id, created_by: user.id,
        ...fields,
        submitted_at: null, approved_at: null, rejected_at: null,
        approver_user_id: null, approval_notes: null, created_job_id: null, job_id: null,
        intake_wizard_step: stepVal ?? 1,
      };
      const created = await requisitionService.create(insert);
      setDraftId(created.id);
      navigate(`/requisitions/${created.id}/intake`, {
        replace: true,
        state: { intakeSeed: created } as IntakeLocationState,
      });
      return created;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  }, [draftId, form, navigate, user]);

  const validateStep1 = () => {
    if (!form.title.trim()) { setError('Role title is required.'); return false; }
    if (!form.client_id) { setError('Select a client.'); return false; }
    if (!isClientReadyForRecruitment(selectedClient)) {
      setError('This client is still in onboarding. Complete compliance on the client record first.');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!form.target_close_date) {
      setError('Target close date is required.');
      return false;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (form.target_close_date <= today) {
      setError('Target close must be in the future.');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!draftId) { if (!validateStep1()) return; await persist(1); return; }
    await persist(step);
  };

  const handleClose = async () => {
    if (!draftId) {
      if (form.title.trim() || form.client_id) { if (!validateStep1()) return; await persist(1); }
    } else {
      await persist(step);
    }
    if (onModalClose) { onModalClose(); return; }
    navigate(originRef.current, { replace: true });
  };

  const handleNext = async () => {
    setError('');
    if (step === 1) {
      if (!validateStep1()) return;
      if (!draftId) { const r = await persist(2); if (r) setStep(2); return; }
      const r = await persist(Math.min(5, step + 1));
      if (r) setStep((s) => Math.min(5, s + 1));
      return;
    }
    if (step === 2) {
      if (!validateStep2()) return;
    }
    if (step < 5) { const r = await persist(step + 1); if (r) setStep((s) => s + 1); }
  };

  const handlePrev = () => {
    setError('');
    if (step <= 1) return;
    const prev = step - 1;
    setStep(prev);
    if (draftId) void persist(prev);
  };

  const handleFinish = async () => {
    if (!draftId) { setError('Use Next or Save on step 1 to create the draft before finishing.'); return; }
    const r = await persist(null, { finish: true });
    if (r) {
      if (onModalClose) onModalClose();
      navigate(`/requisitions/${r.id}?focus=client`);
    }
  };

  const addMust = () => {
    const s = mustInput.trim();
    if (s && !form.must_have_skills.includes(s)) setForm((f) => ({ ...f, must_have_skills: [...f.must_have_skills, s] }));
    setMustInput('');
  };
  const addNice = () => {
    const s = niceInput.trim();
    if (s && !form.nice_to_have_skills.includes(s)) setForm((f) => ({ ...f, nice_to_have_skills: [...f.nice_to_have_skills, s] }));
    setNiceInput('');
  };

  // Block Escape — user must use Close button
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') e.preventDefault(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!user?.orgId) return null;

  // Phase 8 T-22 §27.B — on the fresh `/requisitions/new` route (no draft id
  // and no title typed yet) render the Assistant intake before the wizard.
  // Submit pre-fills the title field so the wizard Step-1 picks it up.
  if (!routeDraftId && !form.title.trim() && REQ_INTAKE_CFG) {
    return (
      <AssistantIntake
        greeting={REQ_INTAKE_CFG.greeting}
        placeholder={REQ_INTAKE_CFG.placeholder}
        scopePill={REQ_INTAKE_CFG.scopePill}
        suggestions={REQ_INTAKE_CFG.suggestions}
        beta={REQ_INTAKE_CFG.beta}
        entityPlural={REQ_INTAKE_CFG.entityPlural}
        firstName={(user?.name ?? user?.email ?? 'there').split(/[ @]/)[0] || 'there'}
        gradient="intake"
        onSubmit={(text) => setForm((f) => ({ ...f, title: text }))}
      />
    );
  }

  const meta = WIZARD_STEPS[step - 1];

  /* ─────────────────── STEP CONTENT ─────────────────── */
  const stepBody = loading ? (
    <div className="flex min-h-[200px] items-center justify-center vscode-text-secondary">
      <Loader2 className="h-8 w-8 animate-spin" />
    </div>
  ) : (
    <div className="space-y-5">
      {/* Step 1 */}
      {step === 1 && (
        <>
          <SectionCard title="Who is hiring">
            <div>
              <label className="label text-xs vscode-text-secondary">Client *</label>
              <select className="input w-full" value={form.client_id} onChange={set('client_id')}>
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </SectionCard>

          <SectionCard title="Role definition">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="label text-xs vscode-text-secondary">Role title *</label>
                <input className="input w-full" value={form.title} onChange={set('title')} placeholder="e.g. Senior Backend Engineer" />
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">Department</label>
                <input className="input w-full" value={form.department} onChange={set('department')} placeholder="e.g. Engineering, Product…" />
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">Headcount</label>
                <input type="number" min={1} className="input w-full" value={form.headcount} onChange={set('headcount')} />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Intake metadata">
            <label className="label text-xs vscode-text-secondary mb-2 block">Priority</label>
            <div className="flex flex-wrap gap-2">
              {REQUISITION_PRIORITIES.map((p) => {
                const cfg = PRIORITY_CONFIG[p];
                const active = form.priority === p;
                return (
                  <button key={p} type="button"
                    onClick={() => setForm((f) => ({ ...f, priority: p }))}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-all"
                    style={{
                      color: active ? '#fff' : cfg.color,
                      backgroundColor: active ? cfg.color : cfg.bg,
                      borderColor: active ? cfg.color : cfg.border,
                      boxShadow: active ? `0 0 0 3px ${cfg.color}25` : undefined,
                    }}>
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </SectionCard>
        </>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <>
          <SectionCard title="Primary hiring manager">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label text-xs vscode-text-secondary">Full name *</label>
                <input className="input w-full" value={form.hiring_manager_name} onChange={set('hiring_manager_name')} placeholder="e.g. Priya Sharma" />
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">Work email *</label>
                <input type="email" className="input w-full" value={form.hiring_manager_email} onChange={set('hiring_manager_email')} placeholder="priya@company.com" />
              </div>
            </div>
            {form.hiring_manager_name && form.hiring_manager_email && (
              <div className="mt-3 flex items-center gap-2 rounded-lg border px-3 py-2"
                style={{ borderColor: '#10b98140', backgroundColor: '#10b98108' }}>
                <span className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: '#10b981' }}>
                  {form.hiring_manager_name.charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold vscode-text-primary truncate">{form.hiring_manager_name}</p>
                  <p className="text-[10px] text-emerald-400 truncate">{form.hiring_manager_email}</p>
                </div>
                <Check className="w-3.5 h-3.5 flex-shrink-0 text-emerald-400 ml-auto" />
              </div>
            )}
          </SectionCard>

          <SectionCard title="Work arrangement">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label text-xs vscode-text-secondary">Location</label>
                <input className="input w-full" value={form.location} onChange={set('location')} placeholder="City, Remote, Hybrid…" />
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">Employment type</label>
                <select className="input w-full" value={form.employment_type} onChange={set('employment_type')}>
                  <option value="">Select type…</option>
                  {['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship', 'Temporary'].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">Target start date</label>
                <input type="date" className="input w-full" value={form.target_start_date} onChange={set('target_start_date')} />
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">
                  Target close date <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="date"
                  className="input w-full"
                  value={form.target_close_date}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={set('target_close_date')}
                  required
                />
                <p className="text-[11px] mt-1" style={{ color: 'var(--vscode-text-tertiary)' }}>
                  Deadline to fill this position. Defaults to {defaultTatDays} days from today.
                </p>
              </div>
            </div>
          </SectionCard>
        </>
      )}

      {/* Step 3 */}
      {step === 3 && (
        <>
          <SectionCard title="Business justification">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs vscode-text-tertiary">Why is this role needed? Describe scope, impact and key responsibilities.</p>
              <button type="button"
                className="flex items-center gap-1 text-xs font-medium rounded px-2 py-0.5"
                style={{ color: BRAND, backgroundColor: `${BRAND}15` }}
                onClick={() => {
                  if (!form.title) return;
                  setForm((f) => ({
                    ...f,
                    business_justification: `We are seeking a ${f.title}${f.department ? ` for the ${f.department} team` : ''}${f.location ? ` based in ${f.location}` : ''}. This role is critical to support ${selectedClient?.name ?? 'the client'}'s growth objectives. The hire will contribute to key deliverables including [describe scope]. Headcount: ${f.headcount}.`,
                  }));
                }}>
                <Sparkles className="w-3 h-3" /> AI draft
              </button>
            </div>
            <textarea className="input w-full min-h-[120px] resize-y" value={form.business_justification}
              onChange={set('business_justification')}
              placeholder="Why is this role needed? Scope of work, key responsibilities, impact on the business…" />
          </SectionCard>

          <SectionCard title="Must-have skills">
            <div className="flex gap-2 mb-2">
              <input className="input flex-1" value={mustInput} onChange={(e) => setMustInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMust())}
                placeholder="Type a skill and press Enter" />
              <button type="button" className="btn-secondary" onClick={addMust}>Add</button>
            </div>
            {form.must_have_skills.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {form.must_have_skills.map((s) => (
                  <button key={s} type="button"
                    onClick={() => setForm((f) => ({ ...f, must_have_skills: f.must_have_skills.filter((x) => x !== s) }))}
                    className="rounded-full px-2.5 py-0.5 text-xs font-medium"
                    style={{ backgroundColor: `${BRAND}20`, color: BRAND, border: `1px solid ${BRAND}40` }}>
                    {s} ×
                  </button>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Nice-to-have skills">
            <div className="flex gap-2 mb-2">
              <input className="input flex-1" value={niceInput} onChange={(e) => setNiceInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addNice())}
                placeholder="Type a skill and press Enter" />
              <button type="button" className="btn-secondary" onClick={addNice}>Add</button>
            </div>
            {form.nice_to_have_skills.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {form.nice_to_have_skills.map((s) => (
                  <button key={s} type="button"
                    onClick={() => setForm((f) => ({ ...f, nice_to_have_skills: f.nice_to_have_skills.filter((x) => x !== s) }))}
                    className="rounded-full px-2.5 py-0.5 text-xs font-medium vscode-text-secondary"
                    style={{ backgroundColor: 'var(--vscode-bg-hover)', border: '1px solid var(--vscode-border-primary)' }}>
                    {s} ×
                  </button>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Internal notes">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs vscode-text-tertiary">Visible only to the internal team.</p>
              <div className="flex items-center gap-2">
                {form.internal_notes && (
                  <button type="button" onClick={() => setForm((f) => ({ ...f, internal_notes: '' }))}
                    className="text-xs px-2 py-0.5 rounded border vscode-text-tertiary"
                    style={{ borderColor: 'var(--vscode-border-primary)', backgroundColor: 'var(--vscode-bg-hover)' }}>
                    Clear ×
                  </button>
                )}
                <span className="flex items-center gap-1 text-xs opacity-50" style={{ color: BRAND }}>
                  <Sparkles className="w-3 h-3" /> AI assist on detail page
                </span>
              </div>
            </div>
            <textarea className="input w-full min-h-[80px] resize-y" value={form.internal_notes}
              onChange={set('internal_notes')} placeholder="Team-internal context, caveats, approvals chain…" />
          </SectionCard>
        </>
      )}

      {/* Step 4 */}
      {step === 4 && (
        <SectionCard title="Salary / budget range">
          <p className="text-xs vscode-text-tertiary mb-4">Internal only — not published to candidates or career sites.</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="label text-xs vscode-text-secondary">Currency</label>
              <input className="input w-full" value={form.budget_currency} onChange={set('budget_currency')} placeholder="INR, USD…" />
            </div>
            <div>
              <label className="label text-xs vscode-text-secondary">Minimum</label>
              <input type="number" className="input w-full" value={form.budget_min} onChange={set('budget_min')} placeholder="0" />
            </div>
            <div>
              <label className="label text-xs vscode-text-secondary">Maximum</label>
              <input type="number" className="input w-full" value={form.budget_max} onChange={set('budget_max')} placeholder="0" />
            </div>
          </div>
          {(form.budget_min || form.budget_max) && (
            <div className="mt-4 rounded-lg px-4 py-3 border"
              style={{ backgroundColor: `${BRAND}08`, borderColor: `${BRAND}30` }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-1">Range preview</p>
              <p className="text-sm font-semibold" style={{ color: BRAND }}>
                {formatBudget(form.budget_min, form.budget_max, form.budget_currency)}
              </p>
            </div>
          )}
        </SectionCard>
      )}

      {/* Step 5 — Review */}
      {step === 5 && (
        <div className="space-y-5">
          {/* Document header */}
          <div className="rounded-xl border overflow-hidden"
            style={{ borderColor: `${BRAND}40`, backgroundColor: `${BRAND}08` }}>
            <div className="px-5 py-4 border-b" style={{ borderColor: `${BRAND}30` }}>
              <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: BRAND }}>
                Hiring Requisition — Draft
              </p>
              <h3 className="text-lg font-bold vscode-text-primary">{form.title || '(untitled role)'}</h3>
              <p className="text-xs vscode-text-tertiary mt-0.5">{selectedClient?.name ?? '—'} · {form.department || 'No department'}</p>
            </div>
            <div className="px-5 py-3 flex flex-wrap gap-3">
              {(() => {
                const pc = PRIORITY_CONFIG[form.priority] ?? PRIORITY_CONFIG.normal;
                return (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: pc.bg, color: pc.color, border: `1px solid ${pc.border}` }}>
                    {pc.label} priority
                  </span>
                );
              })()}
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold"
                style={{ backgroundColor: 'var(--vscode-bg-hover)', color: 'var(--vscode-text-secondary)', border: '1px solid var(--vscode-border-primary)' }}>
                HC: {form.headcount}
              </span>
              {form.employment_type && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: 'var(--vscode-bg-hover)', color: 'var(--vscode-text-secondary)', border: '1px solid var(--vscode-border-primary)' }}>
                  {form.employment_type}
                </span>
              )}
            </div>
          </div>

          {/* Role details */}
          <SectionCard title="Role details">
            <dl className="grid gap-3 sm:grid-cols-2">
              <ReviewRow label="Location" value={form.location} />
              <ReviewRow label="Target start" value={form.target_start_date} />
              <ReviewRow label="Target close" value={form.target_close_date} />
              <ReviewRow label="Budget" value={formatBudget(form.budget_min, form.budget_max, form.budget_currency)} />
              <ReviewRow label="Department" value={form.department} />
            </dl>
          </SectionCard>

          {/* Hiring contact */}
          <SectionCard title="Hiring contact">
            <dl className="grid gap-3 sm:grid-cols-2">
              <ReviewRow label="Name" value={form.hiring_manager_name} />
              <ReviewRow label="Email" value={form.hiring_manager_email} />
            </dl>
          </SectionCard>

          {/* Justification */}
          {form.business_justification && (
            <SectionCard title="Business justification">
              <p className="text-sm vscode-text-primary leading-relaxed whitespace-pre-wrap">{form.business_justification}</p>
            </SectionCard>
          )}

          {/* Skills */}
          {(form.must_have_skills.length > 0 || form.nice_to_have_skills.length > 0) && (
            <SectionCard title="Required skills">
              {form.must_have_skills.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-1.5">Must-have</p>
                  <div className="flex flex-wrap gap-1.5">
                    {form.must_have_skills.map((s) => (
                      <span key={s} className="px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: `${BRAND}20`, color: BRAND, border: `1px solid ${BRAND}40` }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {form.nice_to_have_skills.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-1.5">Nice-to-have</p>
                  <div className="flex flex-wrap gap-1.5">
                    {form.nice_to_have_skills.map((s) => (
                      <span key={s} className="px-2.5 py-0.5 rounded-full text-xs font-medium vscode-text-secondary"
                        style={{ backgroundColor: 'var(--vscode-bg-hover)', border: '1px solid var(--vscode-border-primary)' }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </SectionCard>
          )}

          {form.internal_notes && (
            <SectionCard title="Internal notes">
              <p className="text-sm vscode-text-secondary leading-relaxed whitespace-pre-wrap">{form.internal_notes}</p>
            </SectionCard>
          )}

          <p className="text-xs vscode-text-tertiary px-1">
            Finishing clears the intake wizard flag and opens the requisition with the hiring-client gate in focus.
          </p>
        </div>
      )}
    </div>
  );

  /* Right-panel tips per step */
  const INTAKE_TIPS: Record<number, string[]> = {
    1: ['Use standard job title formats (e.g. "Senior React Engineer")', 'Select the correct client — this drives the hiring-client email flow', 'Set headcount accurately — each seat needs a separate sourcing track'],
    2: ['Hiring manager email is used to send the client gate outreach email', 'Target close date drives the TAT report — adjust if the org default is too tight', '"Contract" vs "Full-time" changes the entire sourcing and legal pathway'],
    3: ['Be specific about the business need — this forms the approval case', 'Must-have skills are filtered in screening; nice-to-have are optional', 'Strong justification increases approval speed'],
    4: ['Budget range is internal only — never shown to candidates', 'Wider range gives recruiters more flexibility in negotiation', 'Currency matters — confirm with the client finance team'],
    5: ['Review all details carefully — this becomes the official intake record', 'Finish Intake opens the requisition detail page for next steps', 'You can still edit after finishing from the Overview tab'],
  };

  /* Mini lifecycle flow for the right panel */
  const LIFECYCLE_STAGES = [
    { key: 'intake',    label: 'Role intake',        active: true },
    { key: 'client',    label: 'Client gate',         active: false },
    { key: 'approval',  label: 'Internal approval',   active: false },
    { key: 'approved',  label: 'Approved',            active: false },
    { key: 'sourcing',  label: 'Active sourcing',     active: false },
    { key: 'filled',    label: 'Filled',              active: false },
  ];

  /* ─────────────────── MODAL SHELL ─────────────────── */
  const modalTree = (
    <div
      className="fixed inset-0 z-[200] flex items-stretch justify-center sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="intake-wizard-title"
    >
      {/* Backdrop — blocks click-through; does NOT navigate */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Dialog panel */}
      <div className="relative z-10 flex h-full w-full max-w-4xl flex-col overflow-hidden border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-primary)] shadow-2xl sm:h-[min(880px,calc(100vh-2rem))] sm:rounded-2xl">

        {/* ── Colored title bar ── */}
        <div className="shrink-0 flex items-center justify-between gap-4 px-5 py-3"
          style={{ background: `linear-gradient(135deg, ${BRAND}, #6c63ff)` }}>
          <div>
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-xs text-white/60">Requisitions</span>
              <span className="text-xs text-white/40">›</span>
              <span className="text-xs font-semibold text-white/90">
                {isResumeRoute ? 'Continue Intake' : 'New Intake'}
              </span>
            </div>
            <h1 id="intake-wizard-title" className="text-base font-bold text-white tracking-tight leading-none">
              Requisition intake
            </h1>
          </div>
          <button type="button"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white border border-white/30 bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-40"
            onClick={() => void handleClose()}
            disabled={saving}>
            <X className="h-3.5 w-3.5" /> Close
          </button>
        </div>

        {/* ── Wizard step tabs ── */}
        <div className="shrink-0 px-5 py-2.5 border-b border-[var(--vscode-border-primary)] flex gap-1.5 flex-wrap"
          style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
          {WIZARD_STEPS.map((s) => {
            const done = s.n < step;
            const active = s.n === step;
            const disabled = !draftId && s.n > 1;
            return (
              <button key={s.n} type="button"
                disabled={disabled}
                onClick={() => { if (disabled) return; setStep(s.n); if (draftId) void persist(s.n); }}
                className="rounded-lg px-3 py-1.5 text-xs font-medium transition-all border"
                style={{
                  color: active ? '#fff' : done ? BRAND : 'var(--vscode-text-tertiary)',
                  backgroundColor: active ? BRAND : done ? `${BRAND}18` : 'transparent',
                  borderColor: active ? BRAND : done ? `${BRAND}40` : 'var(--vscode-border-primary)',
                  opacity: disabled ? 0.4 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}>
                {done && <Check className="w-3 h-3 inline mr-1" />}
                {s.n}. {s.title}
              </button>
            );
          })}
        </div>

        {/* ── Step heading ── */}
        <div className="shrink-0 px-5 pt-3 pb-2 flex items-center gap-3 border-b border-[var(--vscode-border-primary)]"
          style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${BRAND}20`, color: BRAND }}>
            <meta.icon className="w-3.5 h-3.5" />
          </div>
          <div>
            <p className="text-sm font-bold vscode-text-primary leading-none">{meta.title}</p>
            <p className="text-xs vscode-text-tertiary mt-0.5">{meta.subtitle}</p>
          </div>
          <span className="ml-auto text-xs font-mono vscode-text-tertiary">{step} / {WIZARD_STEPS.length}</span>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="shrink-0 mx-5 mt-2 flex flex-wrap items-center gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2">
            <p className="text-sm text-red-500 flex-1">{error}</p>
            {error.includes('Could not load') && (
              <button type="button" className="btn-secondary text-xs" onClick={() => { setError(''); setLoadAttempt((n) => n + 1); }}>Retry</button>
            )}
          </div>
        )}

        {/* ── Two-column body ── */}
        <div className="flex-1 min-h-0 overflow-hidden flex gap-0">

          {/* Left: scrollable form */}
          <div className="flex-1 min-w-0 overflow-y-auto px-5 py-4">
            {stepBody}
          </div>

          {/* Right: lifecycle + tips panel */}
          <div className="w-[210px] flex-shrink-0 border-l border-[var(--vscode-border-primary)] overflow-y-auto px-4 py-4 space-y-5"
            style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>

            {/* Mini lifecycle track */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: BRAND }}>
                Requisition flow
              </p>
              <ul className="space-y-0">
                {LIFECYCLE_STAGES.map((stage, i) => (
                  <li key={stage.key} className="flex gap-2 items-start">
                    <div className="flex flex-col items-center w-5 flex-shrink-0 mt-0.5">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          backgroundColor: stage.active ? BRAND : 'var(--vscode-bg-hover)',
                          border: `2px solid ${stage.active ? BRAND : 'var(--vscode-border-primary)'}`,
                          boxShadow: stage.active ? `0 0 0 3px ${BRAND}28` : undefined,
                        }}>
                        {stage.active && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                      {i < LIFECYCLE_STAGES.length - 1 && (
                        <div className="w-0.5 h-4 rounded-full mt-0.5"
                          style={{ backgroundColor: 'var(--vscode-border-primary)' }} />
                      )}
                    </div>
                    <p className="text-xs pb-3 leading-none mt-0.5"
                      style={{ color: stage.active ? BRAND : 'var(--vscode-text-tertiary)', fontWeight: stage.active ? 600 : 400 }}>
                      {stage.label}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-[var(--vscode-border-primary)]" />

            {/* Step tips */}
            {INTAKE_TIPS[step] && (
              <div className="space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-widest vscode-text-tertiary">Tips</p>
                {INTAKE_TIPS[step].map((tip) => (
                  <p key={tip} className="text-xs vscode-text-tertiary leading-relaxed border-l-2 pl-2"
                    style={{ borderColor: `${BRAND}60` }}>
                    {tip}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="shrink-0 border-t border-[var(--vscode-border-primary)] px-5 py-3"
          style={{ backgroundColor: 'var(--vscode-bg-secondary)' }}>
          <div className="flex items-center justify-between gap-3">
            <button type="button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors vscode-text-secondary hover:vscode-text-primary disabled:opacity-40"
              style={{ borderColor: 'var(--vscode-border-primary)', backgroundColor: 'var(--vscode-bg-elevated)' }}
              onClick={() => void handlePrev()}
              disabled={saving || step <= 1}>
              <ArrowLeft className="h-4 w-4" /> Previous
            </button>

            <div className="flex items-center gap-2">
              <button type="button"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors vscode-text-secondary hover:vscode-text-primary disabled:opacity-40"
                style={{ borderColor: 'var(--vscode-border-primary)', backgroundColor: 'var(--vscode-bg-elevated)' }}
                onClick={() => void handleSave()}
                disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </button>

              {step < WIZARD_STEPS.length ? (
                <button type="button"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40"
                  style={{ backgroundColor: BRAND, boxShadow: `0 2px 8px ${BRAND}40` }}
                  onClick={() => void handleNext()}
                  disabled={saving}>
                  Next <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button type="button"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40"
                  style={{ backgroundColor: '#10b981', boxShadow: '0 2px 8px #10b98140' }}
                  onClick={() => void handleFinish()}
                  disabled={saving || !draftId}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Finish Intake
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modalTree, document.body);
}
